import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { AddressSnapshot, FulfilmentDto, OrderDto, ReleaseOrderResult } from '@thebb/shared';
import { get, patch, post } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, PageTitle, Spinner, fmtTime } from '../components/ui';
import { deliveryLabel, money, navisionLabel, navisionTone, paymentLabel, statusLabel, statusTone } from './Orders';
import { fulfilmentStatusLabel, fulfilmentTone } from './Floor';
import { OrderShipments } from './Shipping';
import { OrderEmails, OrderPaymentCard } from './Payments';
import { CreateReturnButton } from './Inbound';
import { OrderInvoices } from './Finance';

function Address({ title, a }: { title: string; a: AddressSnapshot | null }) {
  return (
    <div className="card text-sm">
      <div className="label">{title}</div>
      {a ? (
        <div className="space-y-0.5">
          {a.company && <div className="font-medium">{a.company}</div>}
          <div>{a.name}</div>
          <div>{a.street1}</div>
          {a.street2 && <div>{a.street2}</div>}
          <div>
            {a.zip} {a.city}
          </div>
          <div>{a.country ?? a.countryCode}</div>
          {a.department && <div className="text-zinc-500">Afd.: {a.department}</div>}
          {a.email && <div className="text-zinc-500">{a.email}</div>}
          {a.phone && <div className="text-zinc-500">{a.phone}</div>}
          {a.customFields.map((c) => (
            <div key={c} className="text-zinc-500">{c}</div>
          ))}
        </div>
      ) : (
        <div className="text-zinc-400">Ingen</div>
      )}
    </div>
  );
}

export function OrderDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();
  const { can } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState('');
  const order = useQuery({ queryKey: ['order', id], queryFn: () => get<OrderDto>(`/orders/${id}`) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['order', id] });
    void qc.invalidateQueries({ queryKey: ['orders'] });
  };
  const action = useMutation({ mutationFn: (v: { action: string; note?: string }) => post(`/orders/${id}/actions`, v), onSuccess: () => { setNote(''); invalidate(); }, onError: setError });
  const saveNote = useMutation({ mutationFn: (internalNote: string) => patch(`/orders/${id}`, { internalNote }), onSuccess: invalidate, onError: setError });
  const fulfilments = useQuery({ queryKey: ['fulfilments', 'order', id], queryFn: () => get<{ items: FulfilmentDto[] }>(`/fulfilments?orderId=${id}&perPage=50`) });
  const [released, setReleased] = useState<ReleaseOrderResult | null>(null);
  const release = useMutation({
    mutationFn: () => post<ReleaseOrderResult>(`/orders/${id}/release`, {}),
    onSuccess: (r) => { setReleased(r); invalidate(); void qc.invalidateQueries({ queryKey: ['fulfilments'] }); void qc.invalidateQueries({ queryKey: ['stock'] }); },
    onError: setError,
  });
  const decide = useMutation({ mutationFn: (d: 'approve' | 'reject') => post(`/orders/${id}/${d}`, {}), onSuccess: invalidate, onError: setError });
  const cancelFulfilment = useMutation({ mutationFn: (fid: string) => post(`/fulfilments/${fid}/cancel`), onSuccess: () => { invalidate(); void qc.invalidateQueries({ queryKey: ['fulfilments'] }); }, onError: setError });

  if (order.isLoading || !order.data) return <Spinner />;
  const o = order.data;
  const steps = ['new', 'released', 'in_progress', 'partially_shipped', 'shipped', 'completed'];
  const stepIndex = steps.indexOf(o.status);

  return (
    <div className="space-y-4">
      <PageTitle
        title={`Ordre ${o.number}`}
        subtitle={o.demo ? <Badge tone="blue">DEMO: ordren forlader aldrig systemet (mails, fragt, betaling og Dinero er testkørsel)</Badge> : undefined}
        actions={
          <>
            <Link to="/orders" className="btn">Tilbage</Link>
            <CreateReturnButton order={o} />
            {o.status === 'awaiting_approval' && can('orders.release') && <button className="btn btn-primary" disabled={decide.isPending} onClick={() => decide.mutate('approve')}>Godkend</button>}
            {o.status === 'awaiting_approval' && can('orders.cancel') && <button className="btn btn-danger" disabled={decide.isPending} onClick={() => confirm('Afvis ordren? Kunden får besked.') && decide.mutate('reject')}>Afvis</button>}
            {can('orders.release') && ['new', 'released', 'partially_shipped'].includes(o.status) && o.type !== 'gift' && (
              <button className="btn btn-primary" disabled={release.isPending} onClick={() => release.mutate()}>{release.isPending ? 'Frigiver…' : 'Frigiv til pluk'}</button>
            )}
            {can('orders.edit') && (o.status === 'new' || o.status === 'released' || o.status === 'needs_check') && (
              <button className="btn" onClick={() => action.mutate({ action: 'hold', note: note || undefined })}>Sæt på hold</button>
            )}
            {can('orders.edit') && o.status === 'on_hold' && (
              <button className="btn" onClick={() => action.mutate({ action: 'release_hold', note: note || undefined })}>Tag af hold</button>
            )}
            {can('orders.resolve') && o.status === 'needs_check' && (
              <button className="btn btn-primary" onClick={() => action.mutate({ action: 'resolve', note: note || undefined })}>Markér som tjekket</button>
            )}
            {can('orders.cancel') && !['shipped', 'completed', 'cancelled'].includes(o.status) && (
              <button className="btn btn-danger" onClick={() => confirm('Annullér ordren?') && action.mutate({ action: 'cancel', note: note || undefined })}>Annullér</button>
            )}
          </>
        }
      />
      <ErrorBox error={error} />
      {o.navision && (
        <section className="card text-sm">
          <div className="mb-2 flex items-center gap-2"><div className="label mb-0">Navision</div><Badge tone={navisionTone(o.navision.state)}>{navisionLabel[o.navision.state]}</Badge><span className="text-xs text-muted">hentet {fmtTime(o.navision.fetchedAt)}</span></div>
          <div className="grid gap-x-6 gap-y-1 md:grid-cols-4">
            <div><span className="text-muted">Salgsordre</span> {o.navision.salesOrderNo ?? '–'}{o.navision.orderDate && <span className="text-muted"> · {o.navision.orderDate}</span>}</div>
            <div><span className="text-muted">Antal</span> {o.navision.qtyShipped} / {o.navision.qtyOrdered} leveret, {o.navision.qtyInvoiced} faktureret</div>
            <div><span className="text-muted">Leverance</span> {o.navision.shipmentNo ?? '–'}{o.navision.shipmentPostedAt && <span className="text-muted"> · {o.navision.shipmentPostedAt}</span>}{o.navision.trackingNo && <div className="text-xs">T&T {o.navision.trackingNo}</div>}</div>
            <div><span className="text-muted">Faktura</span> {o.navision.invoiceNo ?? '–'}{o.navision.invoicePostedAt && <span className="text-muted"> · {o.navision.invoicePostedAt}</span>}{o.navision.invoiceAmountMinor !== null && <div className="text-xs">{money(o.navision.invoiceAmountMinor, 'DKK')}</div>}</div>
          </div>
        </section>
      )}
      {o.autoReleaseBlock && o.status === 'new' && <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">Ikke frigivet automatisk: {o.autoReleaseBlock} (prøvet {fmtTime(o.autoReleaseAttemptAt)})</div>}
      {released && (
        <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {released.fulfilmentId ? <>Frigivet som <b>{released.number}</b> i {released.warehouseCode}: {released.linesAllocated} linjer til pluk{released.linesBackordered > 0 && <>, {released.linesBackordered} i restordre ({released.backorder.map((b) => `${b.sku} ${b.quantity} stk, ${b.available} disponibel`).join('; ')})</>}.</> : 'Intet frigivet.'}
        </div>
      )}
      {(fulfilments.data?.items.length ?? 0) > 0 && (
        <section className="card text-sm">
          <div className="label">Ekspeditioner</div>
          <table className="table">
            <thead><tr><th>Nr.</th><th>Lager</th><th>Status</th><th className="text-right">Linjer</th><th className="text-right">Stk</th><th>Frigivet</th><th>Plukket</th><th>Sendt</th><th>Track & trace</th><th></th></tr></thead>
            <tbody>
              {fulfilments.data!.items.map((f) => (
                <tr key={f.id}>
                  <td><Link to={['picked', 'packing', 'packed', 'shipped'].includes(f.status) ? '/pack/$id' : '/pick/$id'} params={{ id: f.id }} className="font-medium text-blue-700 hover:underline">{f.number}</Link></td>
                  <td>{f.warehouseCode}</td>
                  <td><Badge tone={fulfilmentTone(f.status)}>{fulfilmentStatusLabel[f.status]}</Badge></td>
                  <td className="text-right tabular-nums">{f.lineCount}</td>
                  <td className="text-right tabular-nums">{f.unitCount}</td>
                  <td className="whitespace-nowrap">{fmtTime(f.releasedAt)}</td>
                  <td className="whitespace-nowrap">{f.pickedBy ? `${f.pickedBy} ${fmtTime(f.pickedAt)}` : ''}</td>
                  <td className="whitespace-nowrap">{fmtTime(f.shippedAt)}</td>
                  <td>{f.trackingNumber ?? ''}</td>
                  <td className="text-right">
                    {f.pickNoteDocumentId && <a className="text-blue-700 hover:underline" href={`/api/documents/${f.pickNoteDocumentId}/download`} target="_blank" rel="noreferrer">plukseddel</a>}{' '}
                    {f.deliveryNoteDocumentId && <a className="text-blue-700 hover:underline" href={`/api/documents/${f.deliveryNoteDocumentId}/download`} target="_blank" rel="noreferrer">følgeseddel</a>}{' '}
                    {can('orders.release') && !['shipped', 'cancelled'].includes(f.status) && <button className="text-xs text-red-700 hover:underline" onClick={() => confirm(`Annullér ${f.number}?`) && cancelFulfilment.mutate(f.id)}>annullér</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <div className="card flex flex-wrap items-center gap-3 text-sm">
        <Badge tone={statusTone(o.status)}>{statusLabel[o.status] ?? o.status}</Badge>
        {o.needsCheckReason && <span className="text-red-700">{o.needsCheckReason}</span>}
        <span className="text-zinc-500">
          {steps.map((s, i) => (
            <span key={s} className={i <= stepIndex ? 'font-medium text-zinc-800' : ''}>
              {i > 0 && ' › '}
              {statusLabel[s]}
            </span>
          ))}
        </span>
        <span className="ml-auto text-zinc-500">
          3x #{o.externalNumber} · {o.shopName} · {o.type === 'gift' ? 'Gave' : o.type === 'points' ? 'Point' : 'Ordre'} · {o.placedAt?.slice(0, 10)}
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Address title="Faktureringsadresse" a={o.billingAddress} />
        <Address title="Leveringsadresse" a={o.shippingAddress} />
        <div className="card space-y-2 text-sm">
          <div>
            <span className="label">Levering</span>
            {deliveryLabel[o.delivery.method] ?? o.delivery.method}
            {o.delivery.provider && ` · ${o.delivery.provider}`}
            {o.delivery.parcelShopId && ` · pakkeshop ${o.delivery.parcelShopId}`}
            {o.delivery.deliveryDate && ` · leveres ${o.delivery.deliveryDate}`}
            {o.delivery.deliveryWeek && ` (uge ${o.delivery.deliveryWeek})`}
            {o.delivery.instructionsFlag && <div className="text-amber-700">Leveringsinstruktioner gælder</div>}
          </div>
          <div>
            <span className="label">Betaling</span>
            {paymentLabel[o.payment.provider] ?? o.payment.provider}
            {o.payment.method && ` · ${o.payment.method}`}
            {o.payment.financialStatus && ` · ${o.payment.financialStatus}`}
            {o.payment.transactionId && <div className="text-zinc-500">txn {o.payment.transactionId}</div>}
          </div>
          <div>
            <span className="label">Faktura</span>
            {o.invoiceStatus}
          </div>
          {o.note && (
            <div>
              <span className="label">Kundens kommentar</span>
              {o.note}
            </div>
          )}
        </div>
      </div>

      <div className="card p-0">
        <table className="table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Vare</th>
              <th className="text-right">Antal</th>
              <th className="text-right">Sendt</th>
              <th className="text-right">Stk.pris</th>
              <th className="text-right">Linje</th>
            </tr>
          </thead>
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.id} className={l.variantId ? '' : 'bg-red-50'}>
                <td className="font-mono text-xs">
                  {l.sku}
                  {!l.variantId && <div className="text-red-700">ukendt SKU</div>}
                </td>
                <td>
                  {l.title}
                  {l.variantTitle && <span className="text-zinc-500"> · {l.variantTitle}</span>}
                </td>
                <td className="text-right tabular-nums">{l.quantity}</td>
                <td className="text-right tabular-nums">{l.fulfilledQty}</td>
                <td className="text-right tabular-nums">{money(l.unitPriceMinor, o.currency)}</td>
                <td className="text-right tabular-nums">{money(l.lineTotalMinor, o.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="text-right text-zinc-500">Varer</td>
              <td className="text-right tabular-nums">{money(o.subtotalMinor, o.currency)}</td>
            </tr>
            <tr>
              <td colSpan={5} className="text-right text-zinc-500">Fragt</td>
              <td className="text-right tabular-nums">{money(o.shippingMinor, o.currency)}</td>
            </tr>
            {o.discountMinor !== 0 && (
              <tr>
                <td colSpan={5} className="text-right text-zinc-500">Rabat</td>
                <td className="text-right tabular-nums">-{money(o.discountMinor, o.currency)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={5} className="text-right font-medium">Total</td>
              <td className="text-right font-medium tabular-nums">{money(o.totalMinor, o.currency)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <OrderShipments orderId={o.id} />
      <OrderPaymentCard orderId={o.id} />
      <OrderInvoices orderId={o.id} />
      <OrderEmails orderId={o.id} fulfilments={fulfilments.data?.items ?? []} />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card space-y-2">
          <div className="label">Intern note</div>
          <textarea className="input" rows={3} defaultValue={o.internalNote ?? ''} onBlur={(e) => e.target.value !== (o.internalNote ?? '') && saveNote.mutate(e.target.value)} disabled={!can('orders.edit')} />
          <div className="label">Note til næste handling</div>
          <input className="input" placeholder="Valgfri begrundelse" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="card">
          <div className="label">Hændelser</div>
          <ul className="space-y-1 text-sm">
            {o.events.map((e) => (
              <li key={e.id}>
                <span className="text-zinc-500">{fmtTime(e.at)}</span> · {e.actorName}
                {e.toStatus && (
                  <>
                    {' '}
                    → <Badge tone={statusTone(e.toStatus)}>{statusLabel[e.toStatus] ?? e.toStatus}</Badge>
                  </>
                )}
                {e.note && <span className="text-zinc-600"> · {e.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
